import { FGAlsoLike } from "@fgtheme/fg-recommendations";

/**
 * @typedef ProductVariantGraphQL
 * @type {{ 
 *  data: { 
 *    nodes: {
 *      id: string, 
 *      title: string,
 *      price: {
 *        amount: string
 *      },
 *      compareAtPrice: {
 *        amount: string
 *      }
 *      product: {
 *        id: string,
 *        title: string,
 *        featuredImage: {
 *          altText: string | null,
 *          url: string
 *        }
 *        tags: string[],
 *        handle: string,
 *        metafield_country: {
 *          value: string
 *        } | null
 *        metafield_states: {
 *          value: string
 *        } | null
 *        metafield_rating: {
 *          value: string
 *        } | null
 *        metafield_rating_count: {
 *          value: string
 *        } | null
 *      }
 *    }[] 
 *  } 
 * }}
 */

class WIAlsoLike extends FGAlsoLike {
  strikethroughEnabled = false;
  /**
   * @type {string | undefined | null}
   */
  strikethroughPrice = null;

  ratingMin = 0;
  ratingCountMin = 0;

  /**
   * @type {any | null}
   */
  swiper = null;

  stateChanging = false;

  constructor() {
    super();
  }

  async connectedCallback() {
    this.strikethroughEnabled = this.dataset.strikethroughEnabled === "true";
    this.strikethroughPrice = this.dataset.strikethroughPrice;
    this.ratingMin = this.dataset.ratingMin ? parseInt(this.dataset.ratingMin, 10) : 0;
    this.ratingCountMin = this.dataset.ratingCountMin ? parseInt(this.dataset.ratingCountMin, 10) : 0;

    await super.connectedCallback();

    const swiperElement = this.querySelector('.swiper');
    const swiperNavPrevElement = this.querySelector('.swiper-button-prev');
    const swiperNavNextElement = this.querySelector('.swiper-button-next');
    const swiperScrollbarElement = this.querySelector('.swiper-scrollbar');

    this.swiper = new Swiper(swiperElement, {
      slidesPerView: "auto",
      scrollbar: {
        el: swiperScrollbarElement
      },
      navigation: {
        addIcons: false,
        prevEl: swiperNavPrevElement,
        nextEl: swiperNavNextElement,
      },
      on: {
        unlock: (/** @type {Swiper} */ sw) => {
          this.classList.add('swiper-unlocked');
          this.classList.remove('swiper-locked');
        },
        lock: (/** @type {Swiper} */ sw) => {
          this.classList.add('swiper-locked');
          this.classList.remove('swiper-unlocked');
        }
      }
    });
    this.handleStateChanged = this.handleStateChanged.bind(this);
    document.addEventListener('fgw_state_change', this.handleStateChanged);
  }

  disconnectedCallback() {
    this.removeListeners();
    if (this.swiper) {
      this.swiper.destroy();
    }
    this.querySelector('[data-container]')?.remove();
  }

  removeListeners() {
    document.removeEventListener('fgw_state_change', this.handleStateChanged);
  }

  /**
   * @param {ProductVariantGraphQL["data"]["nodes"][0]} item 
   */
  getProductRatingData(item) {
    const output = {
      stars: [0, 0, 0, 0, 0],
      rating: 0,
      ratingCount: 0
    };

    try {
      // No ratings get zero stars back
      if (!item.product.metafield_rating || !item.product.metafield_rating.value) {
        return output;
      }
      /**
      * @type {{scale_max: string, scale_min: string, value: string}}
      */
      const ratingData = JSON.parse(item.product.metafield_rating.value);
      const ratingCount = item.product.metafield_rating_count
        ? parseInt(item.product.metafield_rating_count.value, 10)
        : 0;
      const ratingValue = parseFloat(ratingData.value);
      output.ratingCount = ratingCount
      output.rating = ratingValue
      if (ratingValue === 0) {
        return output;
      }
      if (ratingValue === 5) {
        output.stars = [1, 1, 1, 1, 1];
        return output;
      }
      const smallestWhole = Math.floor(ratingValue);
      const remainder = +(ratingValue - smallestWhole).toFixed(2);
      let adjustedRemainder = 0;
      if (remainder >= 0.3 && remainder <= 0.7) {
        adjustedRemainder = 0.5;
      } else if (remainder < 0.3) {
        adjustedRemainder = 0;
      } else {
        adjustedRemainder = 1;
      }
      const endZeroStars = smallestWhole < 4 ? 4 - smallestWhole : 0;
      const starData = [];
      // Fill the beginning of the array with the full stars
      for (let ii = 0; ii < smallestWhole; ii++) {
        starData.push(1);
      }
      // Add the remainder star
      starData.push(adjustedRemainder);
      // Add the additional stars needed to hit 5 stars
      for (let ii = 0; ii < endZeroStars; ii++) {
        starData.push(0);
      }
      output.stars = starData;
      return output;
    } catch (e) {
      // This really shouldn't happen as it's a shopify managed metafield,
      // but log an error and return zero stars just in case
      console.error('Error parsing review data', e);
      return output;
    }
  }

  /**
   * Override to customize card per component
   * @param {ProductVariantGraphQL["data"]["nodes"][0]} item 
   * @param {number} index 
   * @param {any} cardTpl 
   */
  customRenderCard(item, index, cardTpl) {
    const ratingData = this.getProductRatingData(item);
    ratingData.stars.forEach(rating => {
      const starTplEl = this.querySelector('[data-tpl="star"]');
      const starTpl = starTplEl.content.cloneNode(true);
      if (rating === 0.5) {
        starTpl.querySelector('path').setAttribute('fill', "url('#ymal-half-star')");
      } else if (rating === 1) {
        starTpl.querySelector('path').setAttribute('fill', 'var(--star-fill-color)');
      } else {
        starTpl.querySelector('path').setAttribute('fill', 'none');
      }
      cardTpl.querySelector('[data-rating]').append(starTpl);
    });
    if (ratingData.rating < this.ratingMin || ratingData.ratingCount < this.ratingCountMin) {
      cardTpl.querySelector('[data-rating]').setAttribute('style', 'opacity: 0;');
    }
    const productLink = cardTpl.querySelector('[data-product-link]');
    productLink.setAttribute('href', `/products/${item.product.handle}`);
    productLink.setAttribute('aria-label', `${item.product.title}`);
    const countryEl = cardTpl.querySelector('[data-meta-country]');
    countryEl.textContent = item.product.metafield_country ? item.product.metafield_country.value : ' ';
  }

  /**
   * 
   * @param {any} event 
   */
  async handleStateChanged(event) {
    // guard against double firing of state change event
    if (this.stateChanging) {
      return;
    }
    this.stateChanging = true;
    this.querySelector('.fg-ymal-container')?.remove();
    this.removeListeners();
    await this.connectedCallback();
    this.stateChanging = false;
  }

  /**
   * 
   * @param {any[]} shopifyRecommendations 
   * @returns {any[]} - filtered array
   */
  filterShopifyProductRecommendations(shopifyRecommendations) {
    const fg_wh = globalThis.FGAvailabilityData.getCurrentWarehouse();

    return shopifyRecommendations.filter(item => {
      // No items of type other than "Bottle"
      if (item.type !== 'Bottle') {
        return false;
      }
      // No unavailable items
      if (!item.available) {
        return false;
      }
      // No items with a selling plan
      if (item.selling_plan_groups.length > 0) {
        return false;
      }
      // No items that require a selling plan
      if (item.requires_selling_plan) {
        return false;
      }
      // No items with multiple variants
      if (item.variants.length > 1) {
        return false;
      }
      // No hidden products
      if (item.tags.includes('hidden-product')) {
        return false;
      }
      // No sold out all items
      if (item.tags.includes('sold-out-all')) {
        return false;
      }
      // No sold out items for the current warehouse
      if (item.tags.includes(`sold-out-${fg_wh.toLowerCase()}`)) {
        return false;
      }
      // No items missing a featured image
      if (!item.featured_image || item.featured_image === '') {
        return false;
      }
      return true;
    });
  }

  getNodesQuery() {
    return `
      query ProductVariantNodes($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            title
            price {
              amount
            }
            compareAtPrice {
              amount
            }
            product {
              id
              title
              featuredImage {
                altText
                url
              }
              tags
              handle
              metafield_country: metafield(namespace: "fgw", key: "country") {
                value
              }
              metafield_states: metafield(namespace: "drinks_assure", key: "available_states") {
                value
              }
              metafield_rating: metafield(namespace: "reviews", key: "rating") {
                value
              }
              metafield_rating_count: metafield(namespace: "reviews", key: "rating_count") {
                value
              }
            }
          }
        }
      }
    `;
  }

  /**
   * 
   * @param {import('@fgtheme/fg-recommendations').FGRecoommendationsProductConfig} productConfig 
   * @param {Set<string>} recommendedVariantIds 
   * @returns {string[]}
   */
  prepareVariantDataIds(productConfig, recommendedVariantIds) {
    // Remove the main product variant id if it was included in recommended products.
    return [...recommendedVariantIds].filter(id => id !== productConfig.mainProduct.variantId);
  }


  modifyRecommendations() {
    const filtered = this.recommendations.filter((/** @type {any} */ item) => {
      if (item.product.metafield_states && item.product.metafield_states.value) {
        const currentState = globalThis.FGAvailabilityData.getCurrentState();
        if (!item.product.metafield_states.value.includes(currentState)) {
          return false;
        }
      }
      return true;
    });

    this.recommendations = filtered;

    this.recommendations.forEach(item => {
      if (item.product.tags && item.product.tags.includes('no-strikethrough')) {
        return;
      }
      if (this.strikethroughEnabled && this.strikethroughPrice) {
        item.price.amount = this.strikethroughPrice;
      }
    })
  }


  getLineItemProperties() {
    return {
      '_fg_widget_id': 'WI-ALSO-LIKE'
    }
  }
}

if (!customElements.get('wi-also-like')) {
  customElements.define('wi-also-like', WIAlsoLike);
}